import type { QuotedLeg } from '../types/trade.types';

/**
 * Clasificación de cada cargo de una cotización por el ACTOR que lo cobra.
 *
 * 🔴 Pedido de Delicia: una cotización de importación es la suma de lo que cobra
 * cada jugador —naviera, NVOCC, transportista, aerolínea, agencia aduanera,
 * aseguradora, agente de carga— más los gastos de liberación de puerto, y todo eso
 * tiene que verse desglosado por actor.
 *
 * ⚠️ Es una HEURÍSTICA, no un dato duro: se deduce del concepto del cargo y del tipo
 * de tramo (marítimo/aéreo/terrestre). Cuando los proveedores del tarifario tengan
 * cargado su `providerType` (el campo que se agregó en el back), se puede volver
 * exacto usando ese tipo en vez de adivinar. Por ahora, esto agrupa bien lo típico.
 */

export type ActorTarifa =
  | 'NAVIERA'
  | 'NVOCC'
  | 'AGENTE_CARGA'
  | 'TRANSPORTISTA'
  | 'AEROLINEA'
  | 'AGENCIA_ADUANERA'
  | 'ASEGURADORA'
  | 'LIBERACION_PUERTO'
  | 'OTRO';

export const ACTOR_LABELS: Record<ActorTarifa, string> = {
  NAVIERA: 'Naviera',
  NVOCC: 'NVOCC',
  AGENTE_CARGA: 'Agente de carga',
  TRANSPORTISTA: 'Transportista',
  AEROLINEA: 'Aerolínea',
  AGENCIA_ADUANERA: 'Agencia aduanera',
  ASEGURADORA: 'Seguro',
  LIBERACION_PUERTO: 'Liberación de puerto',
  OTRO: 'Otros',
};

const norm = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** El actor del "flete base" de un tramo, cuando el concepto no lo aclara. */
export function actorBase(legType: string, transportMode: string): ActorTarifa {
  if (transportMode === 'AIR') return 'AEROLINEA';
  if (legType === 'INLAND_ORIGIN' || legType === 'INLAND_DESTINATION' || transportMode === 'LAND')
    return 'TRANSPORTISTA';
  if (legType === 'HANDLING') return 'LIBERACION_PUERTO';
  if (transportMode === 'SEA') return 'NAVIERA';
  return 'OTRO';
}

/**
 * A qué actor corresponde un cargo, mirando primero el concepto (la señal más
 * fuerte) y cayendo al actor del flete base del tramo si el concepto no aclara.
 */
export function actorDeLinea(concept: string, legType: string, transportMode: string): ActorTarifa {
  const c = norm(concept);
  if (/seguro|insurance/.test(c)) return 'ASEGURADORA';
  if (/liberac|puerto|almacena|storage|thc terrestre destino/.test(c)) return 'LIBERACION_PUERTO';
  if (/aduana|despacho|desaduan|nacionaliz|\bdui\b|poliza|aforo/.test(c)) return 'AGENCIA_ADUANERA';
  if (/nvocc/.test(c)) return 'NVOCC';
  if (/agente|agency|forwarder|profit\s*agente|comision\s*agente|honorarios/.test(c))
    return 'AGENTE_CARGA';
  if (/camion|terrestre|trucking|inland|transporte|acarreo|flete interno/.test(c))
    return 'TRANSPORTISTA';
  if (/aereo|air\s*freight|awb|aerolinea/.test(c)) return 'AEROLINEA';
  // El flete base, THC, BL, manifiesto, etc. → el actor del tramo.
  return actorBase(legType, transportMode);
}

export interface LineaPorActor {
  actor: ActorTarifa;
  label: string;
  total: number;
}

/**
 * Suma el COSTO de la cotización agrupado por actor. Trabaja sobre los componentes
 * del tarifario de cada tramo (concepto + monto); si un tramo no trae desglose, usa
 * su costo como "flete base". El total resultante es el costo (sin el margen de
 * ABCD, que va aparte). Devuelto ordenado de mayor a menor.
 */
export function resumenPorActor(legs: QuotedLeg[]): LineaPorActor[] {
  const acum = new Map<ActorTarifa, number>();
  for (const leg of legs) {
    const lineas =
      leg.components.length > 0
        ? leg.components
        : [{ concept: 'Flete', amount: leg.costAmount, payableAt: 'LOCAL' as const }];
    for (const c of lineas) {
      const actor = actorDeLinea(c.concept, leg.legType, leg.transportMode);
      acum.set(actor, (acum.get(actor) ?? 0) + c.amount);
    }
  }
  return [...acum.entries()]
    .map(([actor, total]) => ({ actor, label: ACTOR_LABELS[actor], total }))
    .filter((l) => Math.abs(l.total) > 0.005)
    .sort((a, b) => b.total - a.total);
}
